/**
 * The delivery log's ranking, filters and grouping — usability audit C6 (2026-10-02).
 *
 * The log had no grouping and no filter, and nothing on the link, while its sibling pages (the
 * execution pack, the backlog) group and filter their commands. `GET /inseason/write-back` takes
 * no filter at all (`limit`, `cursor` only) and the page reads every page of it, so everything
 * here is matched over the rows read:
 *
 *   ranking   the latest observed first (the log's own order), the oldest raised, or by state in
 *             the order a command moves (`STATE_ORDER`)
 *   filters   market · delivery state · kind · target · SLA (elapsed / within / none configured)
 *             · command or subject (text) — `?rank=`, `?f.<id>=`
 *   grouping  «Raggruppa per» (`?by=state|kind|target|market`): one line per value with its
 *             commands, executed, in transit and needing attention — the same four counts as the
 *             figures, never adding `delivered` to `executed` (§14 E10) — and a link that filters
 *             the log to it
 *
 * Pure: no React, no `window`.
 */

import type { TFunction } from 'i18next';

import type { WriteBackState } from '@/api/generated';
import { formatDateTime } from '@/lib/format';
import type { RankedCapConfig } from '@/planning';

import { isAcknowledged, isExecuted, isInTransit, needsAttention, toDeliveryStateKey } from './api';
import { STATE_ORDER } from './write-back-groups';

export const LOG_GROUP_PARAM = 'by';

export const LOG_GROUPINGS = ['state', 'kind', 'target', 'market'] as const;
export type LogGrouping = (typeof LOG_GROUPINGS)[number];

/** The filter each grouping sets when a group is opened. */
export const GROUPING_FILTER: Readonly<Record<LogGrouping, string>> = {
  state: 'state',
  kind: 'kind',
  target: 'target',
  market: 'market',
};

/** «Raggruppa per» as the link states it, or `null` for no grouping. Pure. */
export function readLogGrouping(search: string): LogGrouping | null {
  const value = new URLSearchParams(search).get(LOG_GROUP_PARAM);
  return value !== null && (LOG_GROUPINGS as readonly string[]).includes(value) ? value as LogGrouping : null;
}

/** Whether the link narrows the log to the commands that need attention (`?needsAttention=true`). Pure. */
export function readNeedsAttention(search: string): boolean {
  // The key is spelled where it is read (the census follows literals); `NEEDS_ATTENTION_KEY` in `./pending-links` is the link's.
  return new URLSearchParams(search).get('needsAttention') === 'true';
}

/** The SLA as the filter names it: three values, the third the absence of a configured SLA. */
export function slaValue(row: Pick<WriteBackState, 'sla_elapsed'>): 'elapsed' | 'within' | 'none' {
  return row.sla_elapsed === true ? 'elapsed' : row.sla_elapsed === false ? 'within' : 'none';
}

/** A state's place in the order a command moves; an unrecognised one after them. */
function stateRank(state: string): number {
  const at = STATE_ORDER.indexOf(state);
  return at === -1 ? STATE_ORDER.length : at;
}

/** A row's value under a grouping. */
export function groupValue(row: WriteBackState, by: LogGrouping): string {
  switch (by) {
    case 'state': return row.state;
    case 'kind': return row.subject_kind;
    case 'target': return row.target;
    case 'market': return row.country_key;
  }
}

/**
 * A command in words, from what its row carries (audit 3): «Proposta di riassortimento · CO · creato
 * il 12 ott 2026, 08:07 UTC». The row carries no run, so the kind, the market and the minute it was
 * raised are all the words there are; the 64-character identifier is behind the «id» beside it, and
 * the «Comando (codice)» filter still takes it. A moment that cannot be read is left out, never
 * printed raw. Pure.
 */
export function commandWords(
  row: Pick<WriteBackState, 'subject_kind' | 'country_key' | 'created_at'>,
  kindWords: (kind: string) => string,
  t: TFunction,
  language: string,
): string {
  const values = { kind: kindWords(row.subject_kind), country: row.country_key };
  if (Number.isNaN(new Date(row.created_at).getTime())) return t('inseason.writeBackLog.commandUndated', values);
  return t('inseason.writeBackLog.command', { ...values, moment: t('idWords.instantUtc', { instant: formatDateTime(row.created_at, language, 'UTC') }) });
}

/**
 * An approval in words, from what the row carries (audit 4): «Approvazione · Proposta di riassortimento
 * · CO · del 12 ott 2026, 08:07 UTC». The row publishes the approval's identifier and nothing else of it,
 * so the words are the command's: its kind, its market and the minute it was raised — the minute
 * its approval minted it, in the same transaction (ADR 6037) — and the 64 characters sit behind the
 * «id» beside it. A moment that cannot be read is left out, never printed raw. Pure.
 */
export function approvalWords(
  row: Pick<WriteBackState, 'subject_kind' | 'country_key' | 'created_at'>,
  kindWords: (kind: string) => string,
  t: TFunction,
  language: string,
): string {
  const values = { kind: kindWords(row.subject_kind), country: row.country_key };
  if (Number.isNaN(new Date(row.created_at).getTime())) return t('inseason.writeBackLog.approvalUndated', values);
  return t('inseason.writeBackLog.approval', { ...values, moment: t('idWords.instantUtc', { instant: formatDateTime(row.created_at, language, 'UTC') }) });
}

/**
 * The destinations the platform writes a command to, in words. `platform.plan-store` is the platform's
 * own plan store, where an approval lands and from where a planner exports it (bata-platform
 * `PLATFORM_WRITE_BACK_TARGET`): nothing leaves the platform, which is what the words say. A
 * destination this table does not name is not guessed at — see {@link targetWords}.
 */
const TARGET_KEY: Readonly<Record<string, string>> = {
  'platform.plan-store': 'inseason.writeBackLog.target.planStore',
};

/** The catalogue key naming a destination, or `null` for one this build does not describe. */
export function targetKey(target: string): string | null {
  return Object.prototype.hasOwnProperty.call(TARGET_KEY, target) ? TARGET_KEY[target] ?? null : null;
}

/**
 * A destination as one line of text, for a filter option, a group line and the line above the table: its
 * words, and for a destination this build does not describe «Destinazione non descritta · <code>» — the
 * code kept, because two such destinations must stay two lines. The table's cell does better (the code
 * behind the «id» button, `TargetName`). Pure.
 */
export function targetWords(target: string, t: TFunction): string {
  const key = targetKey(target);
  return key === null ? t('inseason.writeBackLog.target.unknownWithCode', { target }) : t(key);
}

export function logRanking(
  t: (key: string) => string, kindWords: (kind: string) => string, targetLabel: (target: string) => string,
): RankedCapConfig<WriteBackState> {
  return {
    defaultRank: 'observed',
    rankOptions: [
      { id: 'observed', labelKey: 'inseason.writeBackLog.rank.observed', value: (row) => row.state_observed_at, direction: 'desc' },
      { id: 'created', labelKey: 'inseason.writeBackLog.rank.created', value: (row) => row.created_at, direction: 'asc' },
      {
        id: 'state', labelKey: 'inseason.column.deliveryState', value: (row) => stateRank(row.state), direction: 'asc',
        tieBreak: { value: (row) => Date.parse(row.state_observed_at), direction: 'desc' },
      },
    ],
    filters: [
      { id: 'market', labelKey: 'inseason.writeBackLog.filter.market', kind: 'select', value: (row) => row.country_key },
      { id: 'state', labelKey: 'inseason.column.deliveryState', kind: 'multi', value: (row) => row.state, label: (value) => t(toDeliveryStateKey(value)) },
      { id: 'kind', labelKey: 'inseason.column.subjectKind', kind: 'select', value: (row) => row.subject_kind, label: kindWords },
      { id: 'target', labelKey: 'inseason.column.target', kind: 'select', value: (row) => row.target, label: targetLabel },
      {
        id: 'sla', labelKey: 'inseason.column.sla', kind: 'select', value: slaValue,
        label: (value) => t(value === 'elapsed' ? 'inseason.sla.elapsed' : value === 'within' ? 'inseason.sla.within' : 'inseason.writeBackLog.slaNone'),
      },
      // Two collectors, not one search box over the two ids joined (client request, 2026-10-06).
      // The join existed so one box could match either; as a collector it would have offered
      // options reading «CMD-1⏎SUBJ-2», which is not a value anybody picked. A planner pastes an
      // id into the narrowing box and collects it, and can hold several at once.
      { id: 'command', labelKey: 'inseason.writeBackLog.filter.commandId', kind: 'multi', value: (row) => row.command_id },
      { id: 'subject', labelKey: 'inseason.writeBackLog.filter.subjectId', kind: 'multi', value: (row) => row.subject_id },
      // The approval is named in words in the table (audit 4); its identifier is collected here.
      { id: 'approval', labelKey: 'inseason.writeBackLog.filter.approvalId', kind: 'multi', value: (row) => row.approval_id },
    ],
  };
}

export interface LogGroup {
  readonly value: string;
  readonly commands: number;
  readonly executed: number;
  readonly acknowledged: number;
  readonly inTransit: number;
  readonly attention: number;
}

/** One line per value of the grouping, the most commands first (then by value). Pure. */
export function logGroups(rows: readonly WriteBackState[], by: LogGrouping): readonly LogGroup[] {
  const groups = new Map<string, { commands: number; executed: number; acknowledged: number; inTransit: number; attention: number }>();
  for (const row of rows) {
    const value = groupValue(row, by);
    const group = groups.get(value) ?? { commands: 0, executed: 0, acknowledged: 0, inTransit: 0, attention: 0 };
    group.commands += 1;
    if (isExecuted(row)) group.executed += 1;
    if (isAcknowledged(row)) group.acknowledged += 1;
    if (isInTransit(row)) group.inTransit += 1;
    if (needsAttention(row)) group.attention += 1;
    groups.set(value, group);
  }
  return [...groups.entries()]
    .map(([value, counts]) => ({ value, ...counts }))
    .sort((a, b) => b.commands - a.commands || a.value.localeCompare(b.value));
}
